import React, { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import InfoView from 'sharedComponents/InfoView';
import InfoViewSkeleton from 'sharedComponents/InfoViewSkeleton';

import { isStaffUser } from 'sharedHelpers/typeNarrowing';
import { alphabetizeClients } from 'sharedHelpers/clientHelpers';

import { useGetUserQuery } from 'generated/graphql';

import useFilterClients from 'sharedHooks/useFilterClients';
import AddClientButton from '../client/components/AddClientButton';
import ClientListPanelItem from '../client/components/ClientListPanelItem';
import Dashboard from './components/Dashboard';

import { clientsList } from '../client/dataTransforms';

import {
  allClientsListVar,
  organizationId,
} from '../../shared/graphql/apolloCache';

interface DashboardPageProps {
  clientId?: string;
}

function DashboardPage({ clientId }: DashboardPageProps): JSX.Element {
  const clients = useReactiveVar(allClientsListVar);
  // dashboard
  const { showClientsDashboardFlag, filterIncludeEventStaff } = useFlags();

  const { data: serverData, loading } = useGetUserQuery({
    variables: {
      filterIncludeEventStaff: filterIncludeEventStaff || false,
    },
  });

  useEffect(() => {
    if (serverData && isStaffUser(serverData.getUser)) {
      const user = serverData.getUser;
      const { id } = user.organization || {};
      allClientsListVar(clientsList(user.clients));
      organizationId(id);
    }
  }, [serverData]);

  const filteredClientsList = useFilterClients({
    clientsList: clients,
    sortFunction: alphabetizeClients,
  });

  return (
    <>
      {loading && <InfoViewSkeleton />}
      {!loading && (
        <InfoView
          selectedItem={clientId}
          listPanelChildren={({ index, style }) => (
            <ClientListPanelItem
              clientsData={filteredClientsList}
              style={style}
              index={index}
            />
          )}
          detailPanelChildren={showClientsDashboardFlag && <Dashboard />}
          title="Dashboard"
          numberOfItems={filteredClientsList.length}
          HeaderButton={<AddClientButton />}
          errorMessage={
            clients.length > filteredClientsList.length
              ? 'There are no clients matching the current filters'
              : 'You have no clients to display'
          }
        />
      )}
    </>
  );
}
DashboardPage.defaultProps = {
  clientId: undefined,
};

export default DashboardPage;
